import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStatusChipSx } from '../../constants/statusColors';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  VisibilityOutlined as VisibilityIcon,
  EditOutlined as EditIcon,
  PersonOutlined as PersonIcon,
  SyncOutlined as SyncIcon,
  CloudUploadOutlined as ImportIcon,
  DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import FilterPanel from '../../components/common/FilterPanel';
import DataTable from '../../components/common/DataTable';
import { csAdminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isGroupScopeRole } from '../../constants/csAdminRoles';
import { mapDirectorFromApi, mapCompanyFromApi } from '../../utils/apiMappers';
import { exportDirectorsToCsv } from '../../utils/directorExport';
import { useSnackbar } from 'notistack';
import { 
  downloadDirectorTemplate, 
  parseImportedRow, 
  validateImportedRow,
  calculateSimilarityScore 
} from '../../utils/directorImportTemplate';
import ImportReviewModal from '../../components/directors/ImportReviewModal';
import * as XLSX from 'xlsx';

const DirectorsList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [directors, setDirectors] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    companyId: '',
    status: '',
    nationality: '',
  });

  // Pagination & Sorting
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Import states
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importedDirectors, setImportedDirectors] = useState([]);
  const [importing, setImporting] = useState(false);

  const canSyncPool = isGroupScopeRole(user?.role);

  useEffect(() => {
    fetchCompanyOptions();
  }, []);

  useEffect(() => {
    fetchDirectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters, pagination.page, sortBy, sortOrder]);

  const fetchCompanyOptions = async () => {
    try {
      const res = await csAdminApi.getCompanies();
      const list = (res.data?.data || []).map(mapCompanyFromApi);
      setCompanyOptions(
        list.map((c) => ({ value: String(c.id), label: c.name }))
      );
    } catch (err) {
      console.error('Error loading company filters:', err);
    }
  };

  const fetchDirectors = async () => {
    try {
      setLoading(true);

      const params = {
        search: searchQuery || undefined,
        company_id: filters.companyId ? Number(filters.companyId) : undefined,
        is_active:
          filters.status === 'ACTIVE'
            ? 1
            : filters.status === 'INACTIVE'
              ? 0
              : undefined,
      };
      const response = await csAdminApi.getDirectors(params);
      let rows = (response.data?.data || []).map(mapDirectorFromApi);

      if (filters.nationality) {
        const nat = filters.nationality.toUpperCase();
        rows = rows.filter((d) => {
          const n = (d.nationality || '').toUpperCase();
          return nat === 'UAE' ? n === 'UAE' || n.includes('EMIRAT') : n && n !== 'UAE';
        });
      }

      const col = sortBy === 'company' ? 'companyName' : sortBy === 'name' ? 'lastName' : sortBy;
      rows.sort((a, b) => {
        const av = (a[col] || '').toString().toLowerCase();
        const bv = (b[col] || '').toString().toLowerCase();
        if (av < bv) return sortOrder === 'asc' ? -1 : 1;
        if (av > bv) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      setDirectors(rows);
      setPagination((prev) => ({
        ...prev,
        total: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / prev.limit)),
      }));
      setError('');
    } catch (err) {
      console.error('Error fetching directors:', err);
      setError('Failed to load directors');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      companyId: '',
      status: '',
      nationality: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === directors.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(directors.map((dir) => dir.id));
    }
  };

  const handleRowClick = (row) => {
    navigate(`/directors/${row.id}`);
  };

  // Define table columns
  const columns = [
    {
      id: 'name',
      label: 'Director Name',
      minWidth: 280,
      sortable: true,
      render: (row) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: '#E3F2FD',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 24, color: '#1976D2' }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.firstName} {row.lastName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {row.emiratesId || row.passportNumber || 'N/A'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'company',
      label: 'Company',
      minWidth: 180,
      sortable: true,
      render: (row) => row.companyName || 'N/A',
    },
    {
      id: 'position',
      label: 'Position',
      minWidth: 150,
      sortable: true,
      render: (row) => row.position || 'N/A',
    },
    {
      id: 'nationality',
      label: 'Nationality',
      minWidth: 120,
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: true,
      render: (row) => (
        <Chip
          label={row.status}
          sx={{
            ...getStatusChipSx(row.status),
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            borderRadius: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
          size="small"
        />
      ),
    },
  ];

  // Define row actions
  const actions = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => navigate(`/directors/${row.id}`),
    },
    {
      label: 'Edit',
      icon: <EditIcon />,
      onClick: (row) => navigate(`/directors/${row.id}/edit`),
    },
  ];

  const handleBulkExport = () => {
    const selected = directors.filter((d) => selectedRows.includes(d.id));
    if (!selected.length) return;
    exportDirectorsToCsv(selected, 'directors-selected.csv');
    enqueueSnackbar(`Exported ${selected.length} director(s)`, { variant: 'success' });
  };

  const handleDownloadTemplate = () => {
    downloadDirectorTemplate();
    enqueueSnackbar('Template downloaded', { variant: 'success' });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet);

          if (rows.length === 0) {
            enqueueSnackbar('No data found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          // Fetch all companies for name-to-ID lookup
          let companiesMap = {};
          try {
            const res = await csAdminApi.getCompanies();
            const companies = (res.data?.data || []).map(mapCompanyFromApi);
            companies.forEach(company => {
              const nameLower = company.name.toLowerCase().trim();
              companiesMap[nameLower] = company.id;
            });
            console.log('Available companies for import:', Object.keys(companiesMap));
          } catch (err) {
            console.error('Error loading companies for import:', err);
            enqueueSnackbar('Failed to load companies. Please try again.', { variant: 'error' });
            setImporting(false);
            return;
          }

          // Parse and validate rows
          const processedDirectors = [];
          for (let i = 0; i < rows.length; i++) {
            const parsed = parseImportedRow(rows[i], i);
            
            // Resolve company name to ID
            let companyId = null;
            let companyLookupError = null;
            
            if (!parsed.companyName || parsed.companyName === '') {
              companyLookupError = 'Company Name is required';
            } else {
              const companyNameLower = parsed.companyName.toLowerCase().trim();
              companyId = companiesMap[companyNameLower];
              
              console.log(`Looking up company: "${parsed.companyName}" -> "${companyNameLower}" -> ID: ${companyId}`);
              
              if (!companyId) {
                const availableCompanies = Object.keys(companiesMap).filter(name => 
                  name.includes(companyNameLower) || companyNameLower.includes(name)
                );
                const suggestion = availableCompanies.length > 0 
                  ? ` Did you mean: ${availableCompanies.join(', ')}?` 
                  : '';
                companyLookupError = `Company "${parsed.companyName}" not found.${suggestion} Please use exact company name from Companies page.`;
              }
            }
            
            // Add resolved company ID to parsed data
            parsed.companyId = companyId;
            parsed.companyLookupError = companyLookupError;
            
            const validation = validateImportedRow(parsed);

            // Determine initial status
            let status = 'ready';
            let errors = [];
            let matches = [];

            if (!validation.isValid) {
              status = 'error';
              errors = validation.errors;
            } else if (parsed.companyLookupError) {
              status = 'error';
              errors = [parsed.companyLookupError];
            } else {
              matches = await findMatchingDirectors(parsed);
              if (matches.length > 0) {
                status = 'duplicate';
              }
            }

            processedDirectors.push({
              ...parsed,
              status,
              errors,
              matches,
            });
          }

          // Show review modal if there are any issues, otherwise import directly
          const hasIssues = processedDirectors.some(d => d.status !== 'ready');
          
          if (hasIssues) {
            setImportedDirectors(processedDirectors);
            setImportModalOpen(true);
          } else {
            // All clean, import directly
            await performImport(processedDirectors);
          }

        } catch (err) {
          console.error('Error processing file:', err);
          enqueueSnackbar('Error processing file. Please check the format.', { variant: 'error' });
        } finally {
          setImporting(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Error reading file:', err);
      enqueueSnackbar('Error reading file', { variant: 'error' });
      setImporting(false);
    }

    // Reset file input
    event.target.value = '';
  };

  const findMatchingDirectors = async (importedDirector) => {
    try {
      // Fetch all directors to compare
      const res = await csAdminApi.getDirectors({ limit: 1000 });
      const allDirectors = (res.data?.data || []).map(mapDirectorFromApi);

      // Calculate similarity scores
      const matches = allDirectors
        .map(existing => ({
          ...existing,
          score: calculateSimilarityScore(importedDirector, existing),
        }))
        .filter(m => m.score >= 50) // Only show matches with 50%+ confidence
        .sort((a, b) => b.score - a.score)
        .slice(0, 3); // Top 3 matches

      return matches;
    } catch (err) {
      console.error('Error finding matches:', err);
      return [];
    }
  };

  const performImport = async (directorsToImport) => {
    try {
      setImporting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const director of directorsToImport) {
        try {
          // Check if this is a mapping or new creation
          if (director.resolution?.action === 'skip') {
            continue;
          }

          if (director.resolution?.existingDirectorId) {
            // Map to existing director (update if needed)
            await csAdminApi.updateDirector(director.resolution.existingDirectorId, {
              // Update fields if needed
            });
          } else {
            // Validate company ID before creating
            if (!director.companyId) {
              throw new Error(director.companyLookupError || 'Company ID is required');
            }
            
            // Create new director
            const response = await csAdminApi.createDirector({
              company_id: parseInt(director.companyId, 10),
              first_name: director.firstName,
              last_name: director.lastName,
              date_of_birth: director.dateOfBirth || null,
              email: director.email || null,
              phone: director.phone || null,
              nationality: director.nationality || null,
              passport_number: director.passportNumber || null,
              emirates_id: director.emiratesId || null,
              gender: director.gender || null,
              position: director.position || null,
            });
            
            // Check response status
            if (response.status !== 201 && response.status !== 200) {
              throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
            }
            
            if (response.data?.success === false) {
              throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
            }
          }
          successCount++;
        } catch (err) {
          console.error(`Error importing director ${director.firstName} ${director.lastName}:`, err);
          const errorMsg = err.response?.data?.error?.message 
            || err.response?.data?.message 
            || err.message 
            || 'Unknown error';
          errors.push(`${director.firstName} ${director.lastName}: ${errorMsg}`);
          errorCount++;
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Successfully imported ${successCount} director(s)`, { variant: 'success' });
        fetchDirectors(); // Refresh the list
      }
      if (errorCount > 0) {
        enqueueSnackbar(`Failed to import ${errorCount} director(s). Check console for details.`, { variant: 'error' });
        // Log detailed errors
        console.error('Import errors:', errors);
        errors.forEach(err => console.error('  -', err));
      }

      setImportModalOpen(false);
    } catch (err) {
      console.error('Error performing import:', err);
      const errorMsg = err.response?.data?.error?.message 
        || err.response?.data?.message 
        || err.message 
        || 'Unknown error';
      enqueueSnackbar(`Error during import: ${errorMsg}`, { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleSyncPool = async () => {
    try {
      setSyncing(true);
      const linksRes = await csAdminApi.backfillPersonRegistry();
      const links = linksRes.data?.data || {};
      const docsRes = await csAdminApi.backfillPersonDocuments();
      const docs = docsRes.data?.data || {};
      enqueueSnackbar(
        `Pool sync complete: ${links.linked ?? 0} linked, ${docs.documents_attached ?? 0} documents attached`,
        { variant: 'success' }
      );
      setSyncDialogOpen(false);
      fetchDirectors();
    } catch (err) {
      console.error('Pool sync failed:', err);
      enqueueSnackbar(
        err.response?.data?.error?.message || 'Failed to sync director pool',
        { variant: 'error' }
      );
    } finally {
      setSyncing(false);
    }
  };

  // Filter configuration
  const filterConfig = [
    {
      name: 'companyId',
      label: 'Company',
      value: filters.companyId,
      options:
        companyOptions.length > 0
          ? companyOptions
          : [
              { value: '1', label: 'Etisalat UAE' },
              { value: '2', label: 'Etisalat Services' },
            ],
    },
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
      ],
    },
    {
      name: 'nationality',
      label: 'Nationality',
      value: filters.nationality,
      options: [
        { value: 'UAE', label: 'UAE' },
        { value: 'OTHER', label: 'Other' },
      ],
    },
  ];

  if (loading && directors.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Directors
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage directors for your companies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
            disabled={importing}
          >
            Import Directors
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
            />
          </Button>
          {canSyncPool && (
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => setSyncDialogOpen(true)}
            >
              Sync Pool
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/directors/add')}
          >
            Add Director
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <FilterPanel
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Bulk Actions */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            {selectedRows.length} selected
          </Typography>
          <Button variant="outlined" size="small" onClick={handleBulkExport}>
            Export Selected
          </Button>
        </Box>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={directors}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        sortBy={sortBy}
        sortOrder={sortOrder}
        actions={actions}
        onRowClick={handleRowClick}
        pagination={pagination}
        onPageChange={(page) => setPagination({ ...pagination, page })}
        selectable
      />

      <Dialog open={syncDialogOpen} onClose={() => !syncing && setSyncDialogOpen(false)}>
        <DialogTitle>Sync director pool</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Import existing Phase 1 directors into the CS Admin person registry so they appear
            when adding directors from the pool. Safe to re-run — already-linked directors are skipped.
            Document metadata (passport, Emirates ID) is synced in a second step.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSyncPool}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
          >
            {syncing ? 'Syncing…' : 'Run sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Review Modal */}
      {importedDirectors.length > 0 && (
        <ImportReviewModal
          open={importModalOpen}
          onClose={() => {
            setImportModalOpen(false);
            setImportedDirectors([]);
          }}
          importedDirectors={importedDirectors}
          onImport={performImport}
        />
      )}
    </Container>
  );
};

export default DirectorsList;
